const CLEENG_API_BASE_PROD = "https://mediastoreapi.cleeng.com";
const CLEENG_API_BASE_SANDBOX = "https://mediastoreapi-sandbox.cleeng.com";
const CLEENG_REFRESH_TOKEN_PATH = "/auths/refresh_token";

export const CLEENG_REFRESH_LEAD_SECONDS = 120;

let refreshInFlight = null;

const decodeBase64Url = (segment) => {
  const normalized = String(segment || "").replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);

  if (typeof atob === "function") {
    return atob(padded);
  }

  return Buffer.from(padded, "base64").toString("utf8");
};

export const decodeJwtExp = (jwt) => {
  const parts = String(jwt || "").split(".");
  if (parts.length < 2) {
    return null;
  }

  try {
    const payload = JSON.parse(decodeBase64Url(parts[1]));
    const exp = Number(payload && payload.exp);
    return Number.isFinite(exp) ? exp : null;
  } catch (error) {
    return null;
  }
};

export const resolveCleengApiBase = (widgetScriptUrl) => {
  const normalized = String(widgetScriptUrl || "").toLowerCase();
  if (normalized.includes("widgets.prod.cleeng.com") || normalized.includes("prod.cleeng.com")) {
    return CLEENG_API_BASE_PROD;
  }

  return CLEENG_API_BASE_SANDBOX;
};

export const getCleengRefreshDelayMs = (
  jwt,
  leadSeconds = CLEENG_REFRESH_LEAD_SECONDS,
  nowMs = Date.now(),
) => {
  const exp = decodeJwtExp(jwt);
  if (!exp) {
    return null;
  }

  const refreshAtMs = (exp - leadSeconds) * 1000;
  const delayMs = refreshAtMs - nowMs;
  return Math.max(0, delayMs);
};

export const isCleengJwtNearExpiry = (
  jwt,
  leadSeconds = CLEENG_REFRESH_LEAD_SECONDS,
  nowMs = Date.now(),
) => {
  const exp = decodeJwtExp(jwt);
  if (!exp) {
    return false;
  }

  const nowSeconds = Math.floor(nowMs / 1000);
  return exp - nowSeconds <= leadSeconds;
};

const parseRefreshResponse = (payload) => {
  const responseData = payload && payload.responseData ? payload.responseData : payload;
  const jwt = responseData && responseData.jwt;
  const refreshToken = responseData && responseData.refreshToken;

  if (!jwt || !refreshToken) {
    throw new Error("Cleeng refresh response missing jwt or refreshToken");
  }

  return { jwt, refreshToken };
};

export const refreshCleengAuthTokens = async (
  refreshToken,
  {
    apiBase,
    fetchImpl = fetch,
    refreshUrl,
  } = {},
) => {
  const normalizedRefreshToken = String(refreshToken || "").trim();
  if (!normalizedRefreshToken) {
    throw new Error("Cleeng refreshToken is required");
  }

  if (refreshInFlight) {
    return refreshInFlight;
  }

  const resolvedRefreshUrl = refreshUrl
    || `${String(apiBase || CLEENG_API_BASE_PROD).replace(/\/$/, "")}${CLEENG_REFRESH_TOKEN_PATH}`;

  refreshInFlight = fetchImpl(resolvedRefreshUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ refreshToken: normalizedRefreshToken }),
  })
    .then(async (response) => {
      const rawBody = await response.text();
      let payload = null;

      try {
        payload = rawBody ? JSON.parse(rawBody) : null;
      } catch (error) {
        throw new Error(`Cleeng refresh returned invalid JSON (HTTP ${response.status})`);
      }

      if (!response.ok) {
        const message = payload && payload.message ? payload.message : `HTTP ${response.status}`;
        // Trello 548: keep the HTTP status on the error so callers can
        // distinguish a transient 429 (back off + retry) from a dead session.
        const error = new Error(`Cleeng refresh failed: ${message}`);
        error.status = response.status;
        throw error;
      }

      return parseRefreshResponse(payload);
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

export const resetCleengRefreshMutexForTests = () => {
  refreshInFlight = null;
};